import { useState } from 'react';
import { login } from '../services/api';
import { useT } from '../i18n';

interface LoginProps {
  onLoggedIn: () => void;
  onNavigate?: (view: 'signup' | 'pricing') => void;
}

function Login({ onLoggedIn, onNavigate }: LoginProps) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      onLoggedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.error.auth'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="mx-auto max-w-md rounded-lg border border-brand-200 bg-white p-8 shadow-sm">
      <h1 className="mb-1 text-2xl font-bold text-brand-900">{t('login.title')}</h1>
      <p className="mb-6 text-sm text-brand-600">{t('login.subtitle')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-brand-700">
            {t('login.email')}
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-brand-300 px-3 py-2 text-brand-900 focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-brand-700">
            {t('login.password')}
          </label>
          <input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-brand-300 px-3 py-2 text-brand-900 focus:border-brand-500 focus:outline-none"
          />
        </div>

        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-md bg-brand-700 px-4 py-2 font-medium text-white hover:bg-brand-800 disabled:opacity-60"
        >
          {loading ? t('login.submitting') : t('login.submit')}
        </button>
      </form>

      <p className="text-center text-sm text-brand-600 mt-4">
        {t('login.noAccount')}{' '}
        <button
          type="button"
          onClick={() => onNavigate?.('signup')}
          className="text-brand-700 font-medium underline"
        >
          {t('login.signupLink')}
        </button>
      </p>
    </section>
  );
}

export default Login;
